import { Vec3 as PcVec3, Color } from 'playcanvas';
import {
  sampleAnimTrackRoute,
  type CameraLike,
  type SampleAnimTrackRouteOptions,
  type Vec3Like,
  type ViewerAnimTrack,
} from '@mindcloudxai/shared';

const ROUTE_COLOR = new Color(1, 0.35, 0.05, 1);
const KEYFRAME_COLOR = new Color(0.1, 0.9, 1, 1);
const PREVIEW_CAMERA_COLOR = new Color(1, 0.9, 0.05, 1);
const CAMERA_PIVOT_COLOR = new Color(1, 1, 1, 1);
const PREVIEW_CAMERA_PIVOT_COLOR = new Color(1, 0.25, 0.75, 1);
const WORLD_UP = new PcVec3(0, 1, 0);
const FALLBACK_FORWARD = new PcVec3(0, 0, -1);
const FALLBACK_RIGHT = new PcVec3(1, 0, 0);
const MIN_MARKER_SIZE = 0.1;
const MAX_MARKER_SIZE = 0.6;
const MARKER_RADIUS_SCALE = 0.035;
const CAMERA_FRUSTUM_MARKER_SIZE = 0.35;

/** 最小化 app 接口，避免 App vs AppBase 跨包类型冲突 */
export interface RouteApp {
  on(name: 'update', fn: () => void): void;
  off(name: 'update', fn: () => void): void;
  renderNextFrame: boolean;
  drawLine?(start: PcVec3, end: PcVec3, color: Color, depthTest?: boolean): void;
}

/** 确认 drawLine 存在后的类型 */
export interface DrawableRouteApp extends RouteApp {
  drawLine(start: PcVec3, end: PcVec3, color: Color, depthTest?: boolean): void;
}

function asDebugLineApp(app: RouteApp): DrawableRouteApp | null {
  return typeof app.drawLine === 'function' ? (app as DrawableRouteApp) : null;
}

export interface TrackRouteCameraPose {
  position: PcVec3;
  target: PcVec3;
  fov?: number;
}

export interface DebugLineSegment {
  start: PcVec3;
  end: PcVec3;
  color: Color;
}

export interface CameraFrustumMarkerOptions {
  color?: Color;
  pivotColor?: Color;
  nearDistanceScale?: number;
  farDistanceScale?: number;
  nearHalfWidthScale?: number;
  nearHalfHeightScale?: number;
  farHalfWidthScale?: number;
  farHalfHeightScale?: number;
  pivotScale?: number;
}

const DEFAULT_CAMERA_FRUSTUM_MARKER: Required<CameraFrustumMarkerOptions> = {
  color: KEYFRAME_COLOR,
  pivotColor: CAMERA_PIVOT_COLOR,
  nearDistanceScale: 0.35,
  farDistanceScale: 1.4,
  nearHalfWidthScale: 0.12,
  nearHalfHeightScale: 0.08,
  farHalfWidthScale: 0.55,
  farHalfHeightScale: 0.35,
  pivotScale: 0.14,
};

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

function resolveMarkerSize(points: PcVec3[]): number {
  if (points.length === 0) return MIN_MARKER_SIZE;
  const center = new PcVec3();
  for (const point of points) center.add(point);
  center.mulScalar(1 / points.length);
  const radius = points.reduce((max, point) => Math.max(max, point.distance(center)), 0);
  return clamp(radius * MARKER_RADIUS_SCALE, MIN_MARKER_SIZE, MAX_MARKER_SIZE);
}

function vecFrom(value: Vec3Like): PcVec3 {
  return new PcVec3(value.x, value.y, value.z);
}

function toRoutePose(pose: CameraLike): TrackRouteCameraPose {
  return {
    position: vecFrom(pose.position),
    target: vecFrom(pose.target),
    fov: pose.fov,
  };
}

function normalized(value: PcVec3, fallback: PcVec3): PcVec3 {
  const len = value.length();
  if (len <= 1e-6) return fallback.clone();
  return value.mulScalar(1 / len);
}

function scaledPoint(base: PcVec3, dir: PcVec3, scale: number): PcVec3 {
  return new PcVec3(base.x + dir.x * scale, base.y + dir.y * scale, base.z + dir.z * scale);
}

function cross(a: PcVec3, b: PcVec3): PcVec3 {
  return new PcVec3(a.y * b.z - a.z * b.y, a.z * b.x - a.x * b.z, a.x * b.y - a.y * b.x);
}

function basisFromPose(pose: TrackRouteCameraPose): { forward: PcVec3; right: PcVec3; up: PcVec3 } {
  const forward = normalized(pose.target.clone().sub(pose.position), FALLBACK_FORWARD);
  const right = normalized(cross(forward, WORLD_UP), FALLBACK_RIGHT);
  return { forward, right, up: normalized(cross(right, forward), WORLD_UP) };
}

function offsetPoint(center: PcVec3, right: PcVec3, up: PcVec3, sx: number, sy: number): PcVec3 {
  return new PcVec3(
    center.x + right.x * sx + up.x * sy,
    center.y + right.y * sx + up.y * sy,
    center.z + right.z * sx + up.z * sy,
  );
}

function line(start: PcVec3, end: PcVec3, color: Color): DebugLineSegment {
  return { start, end, color };
}

function drawSegments(app: DrawableRouteApp, segments: DebugLineSegment[]): void {
  for (const segment of segments) {
    app.drawLine(segment.start, segment.end, segment.color, false);
  }
}

function resolveCameraFrustumMarkerOptions(
  options: CameraFrustumMarkerOptions,
): Required<CameraFrustumMarkerOptions> {
  return {
    color: options.color ?? DEFAULT_CAMERA_FRUSTUM_MARKER.color,
    pivotColor: options.pivotColor ?? DEFAULT_CAMERA_FRUSTUM_MARKER.pivotColor,
    nearDistanceScale: options.nearDistanceScale ?? DEFAULT_CAMERA_FRUSTUM_MARKER.nearDistanceScale,
    farDistanceScale: options.farDistanceScale ?? DEFAULT_CAMERA_FRUSTUM_MARKER.farDistanceScale,
    nearHalfWidthScale:
      options.nearHalfWidthScale ?? DEFAULT_CAMERA_FRUSTUM_MARKER.nearHalfWidthScale,
    nearHalfHeightScale:
      options.nearHalfHeightScale ?? DEFAULT_CAMERA_FRUSTUM_MARKER.nearHalfHeightScale,
    farHalfWidthScale: options.farHalfWidthScale ?? DEFAULT_CAMERA_FRUSTUM_MARKER.farHalfWidthScale,
    farHalfHeightScale:
      options.farHalfHeightScale ?? DEFAULT_CAMERA_FRUSTUM_MARKER.farHalfHeightScale,
    pivotScale: options.pivotScale ?? DEFAULT_CAMERA_FRUSTUM_MARKER.pivotScale,
  };
}

export function buildCameraFrustumSegments(
  pose: TrackRouteCameraPose,
  size: number,
  options: CameraFrustumMarkerOptions = {},
): DebugLineSegment[] {
  const marker = resolveCameraFrustumMarkerOptions(options);
  const position = pose.position;
  const { forward, right, up } = basisFromPose(pose);
  const fovScale = Math.max(0.75, Math.min(1.5, Math.tan(((pose.fov ?? 60) * Math.PI) / 360)));
  const nearCenter = scaledPoint(position, forward, size * marker.nearDistanceScale);
  const farCenter = scaledPoint(position, forward, size * marker.farDistanceScale);
  const nearHalfWidth = size * marker.nearHalfWidthScale * fovScale;
  const nearHalfHeight = size * marker.nearHalfHeightScale;
  const farHalfWidth = size * marker.farHalfWidthScale * fovScale;
  const farHalfHeight = size * marker.farHalfHeightScale;
  const near = [
    offsetPoint(nearCenter, right, up, -nearHalfWidth, -nearHalfHeight),
    offsetPoint(nearCenter, right, up, nearHalfWidth, -nearHalfHeight),
    offsetPoint(nearCenter, right, up, nearHalfWidth, nearHalfHeight),
    offsetPoint(nearCenter, right, up, -nearHalfWidth, nearHalfHeight),
  ];
  const far = [
    offsetPoint(farCenter, right, up, -farHalfWidth, -farHalfHeight),
    offsetPoint(farCenter, right, up, farHalfWidth, -farHalfHeight),
    offsetPoint(farCenter, right, up, farHalfWidth, farHalfHeight),
    offsetPoint(farCenter, right, up, -farHalfWidth, farHalfHeight),
  ];
  const pivotSize = size * marker.pivotScale;
  const segments = [
    line(
      scaledPoint(position, right, -pivotSize),
      scaledPoint(position, right, pivotSize),
      marker.pivotColor,
    ),
    line(
      scaledPoint(position, up, -pivotSize),
      scaledPoint(position, up, pivotSize),
      marker.pivotColor,
    ),
  ];
  for (let i = 0; i < 4; i += 1) {
    const next = (i + 1) % 4;
    segments.push(
      line(near[i], near[next], marker.color),
      line(far[i], far[next], marker.color),
      line(position, far[i], marker.color),
    );
  }
  return segments;
}

function buildPointMarkerSegments(points: PcVec3[], size: number): DebugLineSegment[] {
  return points.flatMap((point) => [
    line(
      new PcVec3(point.x - size, point.y, point.z),
      new PcVec3(point.x + size, point.y, point.z),
      KEYFRAME_COLOR,
    ),
    line(
      new PcVec3(point.x, point.y - size, point.z),
      new PcVec3(point.x, point.y + size, point.z),
      KEYFRAME_COLOR,
    ),
  ]);
}

export function drawCameraFrustumMarker(
  app: DrawableRouteApp,
  pose: TrackRouteCameraPose,
  size: number,
): void {
  drawSegments(app, buildCameraFrustumSegments(pose, size));
}

/**
 * 动态运镜路径轨迹覆盖层，可在运行期更新点位或清除。
 * 与 track-route-debug.ts 的区别：后者仅在 loadScene 时创建一次，
 * 本模块支持 showTrackRoute API 动态调用。
 */
export class TrackRouteOverlay {
  private app: RouteApp;
  private debugApp: DrawableRouteApp | null;
  private points: PcVec3[] = [];
  private routeSegments: DebugLineSegment[] = [];
  private markerSegments: DebugLineSegment[] = [];
  private previewSegments: DebugLineSegment[] = [];
  private active = false;
  private onUpdate: () => void;

  constructor(app: RouteApp) {
    this.app = app;
    this.debugApp = asDebugLineApp(app);
    // update 回调只画线，不强制 renderNextFrame；
    // renderNextFrame 在 update() 设置点位时一次性触发即可
    this.onUpdate = () => {
      if (
        !this.active ||
        !this.debugApp ||
        (this.routeSegments.length === 0 &&
          this.markerSegments.length === 0 &&
          this.previewSegments.length === 0)
      )
        return;
      drawSegments(this.debugApp, this.routeSegments);
      drawSegments(this.debugApp, this.markerSegments);
      drawSegments(this.debugApp, this.previewSegments);
    };
  }

  update(input: Vec3Like[] | null): void {
    if (!input || input.length < 2) {
      this.clear();
      return;
    }
    this.points = input.map(vecFrom);
    const markerSize = resolveMarkerSize(this.points);
    this.routeSegments = this.buildRouteSegments();
    this.markerSegments = buildPointMarkerSegments(this.points, markerSize);
    this.previewSegments = [];
    this.activate();
  }

  updateCameraPoses(input: CameraLike[] | null): void {
    if (!input || input.length < 1) {
      this.clear();
      return;
    }
    const poses = input.map(toRoutePose);
    this.points = poses.map((pose) => pose.position);
    this.routeSegments = this.buildRouteSegments();
    this.markerSegments = poses.flatMap((pose) =>
      buildCameraFrustumSegments(pose, CAMERA_FRUSTUM_MARKER_SIZE),
    );
    this.previewSegments = [];
    this.activate();
  }

  updateAnimTrack(track: ViewerAnimTrack | null, options?: SampleAnimTrackRouteOptions): void {
    if (!track) {
      this.clear();
      return;
    }
    const { route, keyframes } = sampleAnimTrackRoute(track, options);
    if (route.length < 2) {
      this.clear();
      return;
    }
    const routePoses = route.map(toRoutePose);
    const keyframePoses = keyframes.map(toRoutePose);
    this.points = routePoses.map((pose) => pose.position);
    this.routeSegments = this.buildRouteSegments();
    this.markerSegments = keyframePoses.flatMap((pose) =>
      buildCameraFrustumSegments(pose, CAMERA_FRUSTUM_MARKER_SIZE),
    );
    this.activate();
  }

  updatePreviewCameraPose(pose: CameraLike | null): void {
    if (!pose) {
      this.previewSegments = [];
      if (this.routeSegments.length === 0 && this.markerSegments.length === 0) {
        this.clear();
        return;
      }
      this.app.renderNextFrame = true;
      return;
    }
    const routePose = toRoutePose(pose);
    this.previewSegments = buildCameraFrustumSegments(routePose, CAMERA_FRUSTUM_MARKER_SIZE, {
      color: PREVIEW_CAMERA_COLOR,
      pivotColor: PREVIEW_CAMERA_PIVOT_COLOR,
    });
    this.activate();
  }

  dispose(): void {
    this.app.off('update', this.onUpdate);
    this.active = false;
    this.points = [];
    this.routeSegments = [];
    this.markerSegments = [];
    this.previewSegments = [];
    this.app.renderNextFrame = true;
  }

  private activate(): void {
    if (!this.active) {
      this.active = true;
      this.app.on('update', this.onUpdate);
    }
    this.app.renderNextFrame = true;
  }

  private clear(): void {
    this.points = [];
    this.routeSegments = [];
    this.markerSegments = [];
    this.previewSegments = [];
    this.active = false;
    this.app.off('update', this.onUpdate);
    this.app.renderNextFrame = true;
  }

  private buildRouteSegments(): DebugLineSegment[] {
    const segments: DebugLineSegment[] = [];
    for (let i = 0; i + 1 < this.points.length; i += 1) {
      segments.push(line(this.points[i], this.points[i + 1], ROUTE_COLOR));
    }
    return segments;
  }
}
